import { useCallback, useEffect, useState } from 'react';

import { useLocation, useMatch, useNavigation, useParams } from 'react-router-dom';

import { useHandlePath } from '@app/routes/hooks/useHandlePath';
import { NavigationState, PathParams } from '@app/routes/types';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import { YAT_PICKER } from '../paths';

export const useHandlePartnerRedirects = () => {
    const { handleCheckPath } = useHandlePath();
    const searchParams = useLocation().search;
    const navigation = useNavigation();

    const { [PathParams.PartnerKey]: partnerKey } = useParams();
    const BASE_PATH = `/partner/${partnerKey}`;

    const isConfirmRoute = useMatch(`${BASE_PATH}/confirm`);
    const isCreateRoute = useMatch(`${BASE_PATH}${YAT_PICKER}`);
    const isManageRoute = useMatch(`${BASE_PATH}/manage`);

    const onRedirectTypeRoute = Boolean(isConfirmRoute || isCreateRoute || isManageRoute);

    const loadingEmojis = useEmojisStore((state) => state.loadingEmojis);
    const emojis = useEmojisStore((state) => state.emojis);

    const [redirectTo, setRedirectTo] = useState<string | undefined>();
    const [pathBasedOnYatAmount, setPathBasedOnYatAmount] = useState<string | undefined>();

    useEffect(() => {
        if (redirectTo || !onRedirectTypeRoute || loadingEmojis) return;

        if ((!emojis || emojis.length === 0) && !isCreateRoute) {
            setPathBasedOnYatAmount(`${BASE_PATH}${YAT_PICKER}${searchParams ?? ''}`);
        }
        if (emojis.length === 1 && !isConfirmRoute) {
            setPathBasedOnYatAmount(`${BASE_PATH}/confirm${searchParams ?? ''}`);
        }
        if (emojis.length > 1 && !isManageRoute) {
            setPathBasedOnYatAmount(`${BASE_PATH}/manage${searchParams ?? ''}`);
        }
    }, [
        BASE_PATH,
        emojis,
        isConfirmRoute,
        isCreateRoute,
        isManageRoute,
        loadingEmojis,
        onRedirectTypeRoute,
        redirectTo,
        searchParams,
    ]);

    const setRedirect = useCallback(
        (path) => {
            const safeRedirect = handleCheckPath(path);
            if (!safeRedirect) {
                setRedirectTo(undefined);
                return;
            }
            if (safeRedirect && navigation.state === NavigationState.Idle) {
                setRedirectTo(safeRedirect);
                return;
            }
        },
        [handleCheckPath, navigation.state],
    );

    useEffect(() => {
        if (!pathBasedOnYatAmount) return;

        setRedirect(pathBasedOnYatAmount);
    }, [pathBasedOnYatAmount, setRedirect]);

    return { redirectTo };
};
